// i18n fragment - populated by the Phase 6 sweep. See ../strings.ts.
// Covers: AppsView, DocumentsView, ConnectView, DeveloperAppsView, DevToolsView.
import type { LocaleStrings } from './base';

export const appsDocsStrings: LocaleStrings = {
  en: {
    // ── web-ui AppsView.vue ──
    'appsview.title':              'Apps',
    'appsview.slots_tip':          'Persistent app slots in use',
    'appsview.state_live':         'Live',
    'appsview.state_booting':      'Booting',
    'appsview.state_paused':       'Paused',
    'appsview.state_failed':       'Failed',
    'appsview.state_archived':     'Archived',
    'appsview.visibility_public':  'Public',
    'appsview.visibility_private': 'Private',
    'appsview.run_mode_dev':       'Dev',
    'appsview.archived_hint':      'Archived - rebuild from its conversation to bring it back',
    'appsview.port_label':         '{slug} on port {port}',
    'appsview.updated':            'Updated {ago}',
    'appsview.created':            'Created {ago}',
    'appsview.open':               'Open',
    'appsview.start':              'Start',
    'appsview.starting':           'Starting…',
    'appsview.studio':             'Studio',
    'appsview.studio_tip':         'Open the conversation that built this app',
    'appsview.destroy_tip':        'Destroy app',
    'appsview.destroy_aria':       'Destroy {slug}',
    'appsview.destroy_title':      'Destroy this app?',
    'appsview.destroy_body':       'This permanently destroys the app "{slug}" - its sandbox, disk, and address. The conversation that built it is kept. This cannot be undone.',
    'appsview.destroy_confirm':    'Destroy app',
    'appsview.destroyed_toast':    'App "{slug}" destroyed',
    'appsview.empty_title':        'No apps yet',
    'appsview.empty_body':         'Apps your agents build show up here, ready to open and share.',
    'appsview.err_start':          'Could not start the app',
    'appsview.ago_s':              '{count}s ago',
    'appsview.ago_min':            '{count} min ago',
    'appsview.ago_h':              '{count} h ago',
    'appsview.ago_d':              '{count} d ago',

    // ── web-ui DocumentsView.vue ──
    'documents.title':                  'Files',
    'documents.tab_documents':          'Documents',
    'documents.tab_generated':          'Generated',
    'documents.file_type_aria':         'File type',
    'documents.upload':                 'Upload',
    'documents.search_docs_ph':         'Search documents…',
    'documents.search_gen_ph':          'Search generated files…',
    'documents.search_aria':            'Search files',
    'documents.scope_aria':             'Visibility filter',
    'documents.scope_all':              'All',
    'documents.scope_private':          'Private',
    'documents.scope_channel':          'Channel',
    'documents.scope_team':             'Team',
    'documents.all_categories':         'All categories',
    'documents.empty_title':            'No documents yet',
    'documents.empty_body':             'Upload a file, or share one in Slack, to make it searchable here.',
    'documents.empty_filtered_title':   'No documents match your filters',
    'documents.clear_filters':          'Clear filters',
    'documents.gen_empty_search':       'No generated files match your search',
    'documents.gen_empty_title':        'No generated files yet',
    'documents.gen_empty_body':         'Files your agents create and deliver show up here, across all conversations.',
    'documents.classification_tip':     'Classification: {status}',
    'documents.resize_aria':            'Resize panels',
    'documents.resize_tip':             'Drag to resize',
    'documents.back_to_list':           'Back to files',
    'documents.download':               'Download',
    'documents.download_aria':          'Download {name}',
    'documents.delete':                 'Delete',
    'documents.visibility_label':       'Visibility',
    'documents.visibility_aria':        'Document visibility',
    'documents.preview_empty_doc':      'Select a document to preview it.',
    'documents.preview_empty_file':     'Select a file to preview it.',
    'documents.conversation':           'Conversation',
    'documents.removed_conversation':   'From a removed conversation',
    'documents.open_conversation':      'Open conversation',
    'documents.from_removed_conversation': 'From a removed conversation',
    'documents.del_file_aria':          'Delete file',
    'documents.del_file_confirm':       'Delete file',
    'documents.del_artifact_title':     'Delete "{name}"?',
    'documents.del_artifact_body':      'This permanently deletes the file. This cannot be undone.',
    'documents.del_artifact_body_conv': 'This removes the file from its conversation and permanently deletes it. This cannot be undone.',
    'documents.file_deleted_toast':     'File deleted',
    'documents.del_doc_title':          'Delete "{name}"?',
    'documents.del_doc_body':           'This permanently deletes the document.{shared} This cannot be undone.',
    'documents.del_shared_team':        ' It is shared with your whole team, so it is removed for everyone.',
    'documents.del_shared_channel':     ' It is shared in {channel}, so it is removed for everyone there.',
    'documents.del_shared_channel_fallback': 'that channel',
    'documents.shared_team_toast':      'Shared with your team',
    'documents.made_private_toast':     'Made private',
    'documents.upload_saved':           'Document saved',
    'documents.upload_dedup':           'Already on file - kept the existing copy',
    'documents.upload_dedup_enriched':  'Already on file - merged your new details into the existing copy',
    'documents.drop_extras_one':        'Uploading "{name}" - one extra file was ignored',
    'documents.drop_extras_other':      'Uploading "{name}" - {count} extra files were ignored',
    'documents.entity_company':         'Company: {name}',
    'documents.entity_person':          'Person: {name}',
    'documents.date_just_now':          'just now',
    'documents.ago_min':                '{count} min ago',
    'documents.ago_h':                  '{count} h ago',
    'documents.ago_d':                  '{count} d ago',
    'documents.drop_title':             'Drop to upload',
    'documents.drop_hint':              'We will take the first file',
    'documents.err_load':               'Failed to load documents',
    'documents.err_load_generated':     'Failed to load generated files',
    'documents.err_delete':             'Failed to delete the document',
    'documents.err_visibility':         'Failed to change visibility',
    'documents.err_open':               'Could not open the document',
    'documents.err_open_no_agent':      'No agent is available to open this document',

    // ── Folders + Trash (DocumentsView.vue) ──
    'documents.tab_trash':              'Trash',
    'documents.folders_aria':           'Folders',
    'documents.root_private':           'My Documents',
    'documents.root_team':              'Team Documents',
    'documents.root_channels':          'Channels',
    'documents.folder_all':             'All',
    'documents.new_folder_tip':         'New folder',
    'documents.new_folder_title':       'New folder',
    'documents.new_folder_label':       'Folder name',
    'documents.new_folder_ph':          'e.g. Invoices',
    'documents.folder_name_empty':      'Enter a name.',
    'documents.folder_created_toast':   'Folder created',
    'documents.err_folder_create':      'Could not create the folder',
    'documents.rename_folder':          'Rename',
    'documents.rename_folder_title':    'Rename folder',
    'documents.rename_folder_label':    'Folder name',
    'documents.folder_renamed_toast':   'Folder renamed',
    'documents.err_folder_rename':      'Could not rename the folder',
    'documents.delete_folder':          'Delete',
    'documents.delete_folder_title':    'Delete "{name}"?',
    'documents.delete_folder_body':     'This moves the folder, its subfolders, and everything inside to Trash.',
    'documents.delete_folder_confirm':  'Move to Trash',
    'documents.folder_trashed_toast':   'Folder moved to Trash',
    'documents.err_folder_delete':      'Could not delete the folder',
    'documents.err_load_folders':       'Could not load folders',
    'documents.folder_doc_count':       '{count} files in this folder',
    'documents.expand_folder':          'Expand',
    'documents.collapse_folder':        'Collapse',
    'documents.folder_actions_aria':    'Folder actions',
    'documents.move_to_folder':         'Move to folder…',
    'documents.move_picker_title':      'Move to folder',
    'documents.move_root':              'Root',
    'documents.moved_toast':            'Moved',
    'documents.err_move':               'Could not move the file',
    'documents.share_team_title':       'Share with your team?',
    'documents.share_team_body':        'Filing this into a team folder shares it with your whole team. Continue?',
    'documents.share_channel_title':    'Share with {channel}?',
    'documents.share_channel_body':     'Filing this into a channel folder shares it in {channel}. Continue?',
    'documents.share_confirm':          'Share',
    'documents.shared_toast':           'File shared',
    'documents.crumb_root':             'All',
    'documents.empty_folder_title':     'This folder is empty',
    'documents.empty_folder_body':      'Move files here, or upload into this folder.',
    'documents.trash_empty_title':      'Trash is empty',
    'documents.trash_empty_body':       'Deleted files land here. They are removed for good when you delete them forever.',
    'documents.restore':                'Restore',
    'documents.restored_toast':         'Restored',
    'documents.err_restore':            'Could not restore the file',
    'documents.delete_forever':         'Delete forever',
    'documents.purge_title':            'Delete "{name}" forever?',
    'documents.purge_body':             'This permanently deletes the file. This cannot be undone.',
    'documents.purge_confirm':          'Delete forever',
    'documents.purge_folder_title':     'Delete folder "{name}" forever?',
    'documents.purge_folder_body':      'This permanently deletes the folder. This cannot be undone.',
    'documents.purge_folder_body_files': 'This permanently deletes the folder and its {count} file(s). This cannot be undone.',
    'documents.purged_toast':           'Deleted forever',
    'documents.err_purge':              'Could not delete the file',
    'documents.search_trash_ph':        'Search trash…',
    'documents.preview_empty_trash':    'Select a trashed file to preview it.',

    // ── web-ui ConnectView.vue ──
    'connect.title':                  'Connect',
    'connect.subtitle':               'Add HQ to your tools and act on what we found for your workspace.',
    'connect.channels_title':         'Channels',
    'connect.channels_lede':          'Add HQ to where your team already works.',
    'connect.connected':              'Connected',
    'connect.slack_connected_toast':  'Slack connected to this workspace.',
    'connect.slack_taken_toast':      'That Slack workspace is already connected to another HQ workspace.',
    'connect.slack_already_toast':    'This workspace already has Slack connected. Disconnect it first to connect a different one.',
    'connect.teams_claimed_toast':    'Microsoft verified. Now add the HQ app in Teams.',
    'connect.teams_connected_toast':  'Teams is already connected to this workspace.',
    'connect.teams_taken_toast':      'That Microsoft org is already connected to another HQ workspace.',
    'connect.teams_error_toast':      'Microsoft sign-in did not complete. Please try again.',
    'connect.teams_verified_lead':    'Verified. Add HQ to Teams to finish:',
    'connect.teams_step_download':    'Download the HQ for Teams app package.',
    'connect.teams_step_upload':      'In Teams admin center -> Manage apps (or Apps -> Manage your apps -> Upload a custom app), upload the package.',
    'connect.teams_step_install':     'Open HQ in Teams and install it - it connects to this workspace automatically.',
    'connect.teams_download_pkg':     'Download HQ for Teams',
    'connect.teams_pkg_unavailable':  'Your HQ contact will share the app package to upload.',
    'connect.coming_soon':            'Coming soon',
    'connect.slack_sub':              'Bring your agents into the channels your team already works in.',
    'connect.slack_reconnect':        'Reconnect',
    'connect.slack_reconnect_hint':   'Re-authorize Slack to grant HQ any newly added permissions.',
    'connect.teams_sub':              'Bring your agents into Microsoft Teams.',
    'connect.teams_add':              'Add to Teams',
    'connect.ext_title':              'Browser extension',
    'connect.ext_sub':                'Let agents read and act on the pages you are viewing.',
    'connect.ext_download':           'Download extension',
    'connect.ext_web_store':          'Get it from the store',
    'connect.recommended_title':      'Recommended for you',
    'connect.recommended_lede':       'Skills and integrations picked from what we found about your workspace.',
    'connect.recommended_empty_title':'Nothing to recommend right now',
    'connect.recommended_empty_body': 'As we learn more about your workspace, suggestions will appear here.',
    'connect.detected_title':         'Detected on your site',
    'connect.detected_lede':          'Signals we picked up while reading your public site and DNS.',
    'connect.detected_email_dns':     'Email & DNS',
    'connect.detected_web_tech':      'Web technology',
    'connect.kind_skill':             'Skill',
    'connect.kind_integration':       'Integration',
    'connect.action_enable':          'Enable',
    'connect.action_setup':           'Set up',
    'connect.priority_high':          'High',
    'connect.priority_medium':        'Medium',
    'connect.priority_low':           'Low',
    'connect.dismiss':                'Dismiss',
    'connect.enabled_toast':          'Enabled {name}',
    'connect.err_enable':             'Could not enable that',
    'connect.load_error_title':       'Could not load Connect',
    'connect.load_error_body':        'Something went wrong fetching your workspace status. Try again shortly.',

    // ── web-ui DeveloperAppsView.vue ──
    'devApps.title':                  'Developer apps',
    'devApps.subtitle':               'OAuth 2.1 clients you have registered to integrate with HQ.',
    'devApps.new_app':                'New app',
    'devApps.hint_base_before':       'The API base is',
    'devApps.hint_minted_before':     '. Tokens are minted at',
    'devApps.hint_api_before':        '. The full reference is in the',
    'devApps.hint_openapi_link':      'OpenAPI spec',
    'devApps.hint_pat_before':        'For a personal token instead of a full app, use',
    'devApps.hint_account_link':      'your account',
    'devApps.reveal_head':            'Save these now - the secret is shown only once.',
    'devApps.client_id':              'Client ID',
    'devApps.client_secret':          'Client secret',
    'devApps.reg_token':              'Registration token',
    'devApps.copy':                   'Copy',
    'devApps.copied':                 'Copied',
    'devApps.saved_them':             'I saved them',
    'devApps.form_title_register':    'Register an app',
    'devApps.form_title_edit':        'Edit app',
    'devApps.field_name_label':       'Name',
    'devApps.field_name_hint':        'Shown to users on the consent screen.',
    'devApps.field_name_ph':          'My integration',
    'devApps.field_redirects_label':  'Redirect URIs',
    'devApps.field_redirects_hint':   'One per line. Where we send users back after they authorize.',
    'devApps.client_type_label':      'Client type',
    'devApps.client_public':          'Public',
    'devApps.client_confidential':    'Confidential',
    'devApps.client_hint_fixed':      'The client type is fixed at registration and cannot be changed.',
    'devApps.client_hint_public':     'For apps that cannot keep a secret (single-page apps, native clients). Uses PKCE, no secret.',
    'devApps.client_hint_confidential':'For server-side apps that can store a secret safely. A client secret is minted.',
    'devApps.scopes_label':           'Scopes',
    'devApps.scopes_hint':            'The permissions this app can request from users.',
    'devApps.cancel':                 'Cancel',
    'devApps.saving':                 'Saving…',
    'devApps.registering':            'Registering…',
    'devApps.save_changes':           'Save changes',
    'devApps.register_app':           'Register app',
    'devApps.empty_title':            'No developer apps yet',
    'devApps.empty_body':             'Register an OAuth client to integrate your own app with HQ.',
    'devApps.redirect_uris_one':      '{count} redirect URI',
    'devApps.redirect_uris_other':    '{count} redirect URIs',
    'devApps.registered':             'Registered {date}',
    'devApps.edit':                   'Edit',
    'devApps.edit_app_tip':           'Edit app',
    'devApps.delete':                 'Delete',
    'devApps.delete_app_tip':         'Delete app',
    'devApps.delete_title':           'Delete app?',
    'devApps.delete_body':            'This permanently deletes "{name}" and revokes its credentials. This cannot be undone.',
    'devApps.delete_confirm':         'Delete app',
    'devApps.deleted_toast':          'App deleted',
    'devApps.updated_toast':          'App updated',
    'devApps.registered_toast':       'App registered',
    'devApps.err_register':           'Failed to register the app',
    'devApps.err_update':             'Failed to update the app',
    'devApps.err_delete':             'Failed to delete the app',
    'devApps.err_copy':               'Could not copy to clipboard',

    // ── web-ui DevToolsView.vue ──
    'devtools.title':                 'Dev tools',
    'devtools.subtitle':              'Spin up throwaway tenants, walk the onboarding surfaces, and tear test data down.',
    'devtools.disabled_before':       'Disabled. Set',
    'devtools.disabled_after':        'on the controlplane to enable these tools.',
    'devtools.disabled_toast':        'Dev tools are disabled on this deploy.',
    'devtools.create_demo':           'Create demo tenant',
    'devtools.send_notification':     'Send test notification',
    'devtools.refresh':               'Refresh',
    'devtools.purge':                 'Purge demo tenants',
    'devtools.col_slug':              'Slug',
    'devtools.col_name':              'Name',
    'devtools.col_source':            'Source',
    'devtools.col_onboarded':         'Onboarded',
    'devtools.col_demo':              'Demo',
    'devtools.yes':                   'Yes',
    'devtools.no':                    'No',
    'devtools.inspect_tip':           'Inspect profiling',
    'devtools.delete_tip':            'Delete tenant',
    'devtools.protected':             'Protected',
    'devtools.inspector_title':       'Profiling inspector',
    'devtools.refetch_tip':           'Refetch profile',
    'devtools.loading_profile':       'Loading profile…',
    'devtools.sec_domain':            'Domain',
    'devtools.domain_confirmed':      'Confirmed',
    'devtools.domain_unconfirmed':    'Unconfirmed',
    'devtools.onboarding_done':       'Onboarded',
    'devtools.onboarding_open':       'Onboarding open',
    'devtools.sec_enrichment':        'Company enrichment',
    'devtools.enrichment_industry':   'Industry:',
    'devtools.enrichment_segment':    'Segment:',
    'devtools.enrichment_size':       'Size:',
    'devtools.sec_brand':             'Brand crawl',
    'devtools.pages_crawled':         '{count} pages crawled',
    'devtools.no_crawl':              'No crawl yet.',
    'devtools.sec_signals':           'Detected signals',
    'devtools.no_signals':            'No signals yet.',
    'devtools.bucket_email_dns':      'Email & DNS',
    'devtools.bucket_web_tech':       'Web technology',
    'devtools.sec_recommendations':   'Recommendations',
    'devtools.no_recommendations':    'No recommendations yet.',
    'devtools.sec_gaps':              'Unmet needs',
    'devtools.no_gaps':               'No gaps detected.',
    'devtools.no_tenants':            'No tenants.',
    'devtools.demand_toggle':         'What customers need next',
    'devtools.demand_sub':            'For every tool customers use that HQ has no connector for, how many distinct tenants want it.',
    'devtools.refresh_demand_tip':    'Refresh demand',
    'devtools.loading_demand':        'Loading demand…',
    'devtools.col_category':          'Category',
    'devtools.col_tool':              'Tool',
    'devtools.col_tenants':           'Tenants',
    'devtools.no_demand':             'No demand signals yet.',
    'devtools.demo_created_toast':    'Demo tenant "{slug}" created',
    'devtools.err_create_demo':       'Could not create a demo tenant',
    'devtools.notify_sent_toast':     'Test notification sent',
    'devtools.notify_signin':         'Sign in to send a test notification.',
    'devtools.err_notify':            'Could not send the test notification',
    'devtools.purge_title':           'Purge demo tenants?',
    'devtools.purge_body':            'This deletes every demo tenant and its data. This cannot be undone.',
    'devtools.purge_confirm':         'Purge',
    'devtools.purged_toast':          'Purged {count} demo tenants',
    'devtools.err_purge':             'Could not purge demo tenants',
    'devtools.delete_title':          'Delete tenant "{slug}"?',
    'devtools.delete_body':           'This deletes the tenant and all of its data. This cannot be undone.',
    'devtools.delete_confirm':        'Delete tenant',
    'devtools.deleted_toast':         'Tenant "{slug}" deleted',
    'devtools.err_delete':            'Could not delete the tenant',
    'devtools.err_load_tenants':      'Could not load tenants',
    'devtools.err_load_profile':      'Could not load the profile',
    'devtools.err_load_demand':       'Could not load demand',
  },
  sv: {
    // ── web-ui AppsView.vue ──
    'appsview.title':              'Appar',
    'appsview.slots_tip':          'Beständiga app-platser i bruk',
    'appsview.state_live':         'Aktiv',
    'appsview.state_booting':      'Startar',
    'appsview.state_paused':       'Pausad',
    'appsview.state_failed':       'Misslyckad',
    'appsview.state_archived':     'Arkiverad',
    'appsview.visibility_public':  'Offentlig',
    'appsview.visibility_private': 'Privat',
    'appsview.run_mode_dev':       'Dev',
    'appsview.archived_hint':      'Arkiverad - bygg om från dess konversation för att ta tillbaka den',
    'appsview.port_label':         '{slug} på port {port}',
    'appsview.updated':            'Uppdaterad {ago}',
    'appsview.created':            'Skapad {ago}',
    'appsview.open':               'Öppna',
    'appsview.start':              'Starta',
    'appsview.starting':           'Startar…',
    'appsview.studio':             'Studio',
    'appsview.studio_tip':         'Öppna konversationen som byggde den här appen',
    'appsview.destroy_tip':        'Förstör app',
    'appsview.destroy_aria':       'Förstör {slug}',
    'appsview.destroy_title':      'Förstöra den här appen?',
    'appsview.destroy_body':       'Detta förstör permanent appen "{slug}" - dess sandbox, disk och adress. Konversationen som byggde den behålls. Detta kan inte ångras.',
    'appsview.destroy_confirm':    'Förstör app',
    'appsview.destroyed_toast':    'Appen "{slug}" förstördes',
    'appsview.empty_title':        'Inga appar än',
    'appsview.empty_body':         'Appar dina agenter bygger visas här, redo att öppnas och delas.',
    'appsview.err_start':          'Det gick inte att starta appen',
    'appsview.ago_s':              'för {count}s sedan',
    'appsview.ago_min':            'för {count} min sedan',
    'appsview.ago_h':              'för {count} tim sedan',
    'appsview.ago_d':              'för {count} d sedan',

    // ── web-ui DocumentsView.vue ──
    'documents.title':                  'Filer',
    'documents.tab_documents':          'Dokument',
    'documents.tab_generated':          'Genererade',
    'documents.file_type_aria':         'Filtyp',
    'documents.upload':                 'Ladda upp',
    'documents.search_docs_ph':         'Sök dokument…',
    'documents.search_gen_ph':          'Sök genererade filer…',
    'documents.search_aria':            'Sök filer',
    'documents.scope_aria':             'Synlighetsfilter',
    'documents.scope_all':              'Alla',
    'documents.scope_private':          'Privat',
    'documents.scope_channel':          'Kanal',
    'documents.scope_team':             'Team',
    'documents.all_categories':         'Alla kategorier',
    'documents.empty_title':            'Inga dokument än',
    'documents.empty_body':             'Ladda upp en fil, eller dela en i Slack, för att göra den sökbar här.',
    'documents.empty_filtered_title':   'Inga dokument matchar dina filter',
    'documents.clear_filters':          'Rensa filter',
    'documents.gen_empty_search':       'Inga genererade filer matchar din sökning',
    'documents.gen_empty_title':        'Inga genererade filer än',
    'documents.gen_empty_body':         'Filer dina agenter skapar och levererar visas här, över alla konversationer.',
    'documents.classification_tip':     'Klassificering: {status}',
    'documents.resize_aria':            'Ändra storlek på paneler',
    'documents.resize_tip':             'Dra för att ändra storlek',
    'documents.back_to_list':           'Tillbaka till filer',
    'documents.download':               'Ladda ner',
    'documents.download_aria':          'Ladda ner {name}',
    'documents.delete':                 'Ta bort',
    'documents.visibility_label':       'Synlighet',
    'documents.visibility_aria':        'Dokumentets synlighet',
    'documents.preview_empty_doc':      'Välj ett dokument för att förhandsgranska det.',
    'documents.preview_empty_file':     'Välj en fil för att förhandsgranska den.',
    'documents.conversation':           'Konversation',
    'documents.removed_conversation':   'Från en borttagen konversation',
    'documents.open_conversation':      'Öppna konversation',
    'documents.from_removed_conversation': 'Från en borttagen konversation',
    'documents.del_file_aria':          'Ta bort fil',
    'documents.del_file_confirm':       'Ta bort fil',
    'documents.del_artifact_title':     'Ta bort "{name}"?',
    'documents.del_artifact_body':      'Detta tar permanent bort filen. Detta kan inte ångras.',
    'documents.del_artifact_body_conv': 'Detta tar bort filen från dess konversation och raderar den permanent. Detta kan inte ångras.',
    'documents.file_deleted_toast':     'Filen togs bort',
    'documents.del_doc_title':          'Ta bort "{name}"?',
    'documents.del_doc_body':           'Detta tar permanent bort dokumentet.{shared} Detta kan inte ångras.',
    'documents.del_shared_team':        ' Det delas med hela ditt team, så det tas bort för alla.',
    'documents.del_shared_channel':     ' Det delas i {channel}, så det tas bort för alla där.',
    'documents.del_shared_channel_fallback': 'den kanalen',
    'documents.shared_team_toast':      'Delat med ditt team',
    'documents.made_private_toast':     'Gjort privat',
    'documents.upload_saved':           'Dokumentet sparades',
    'documents.upload_dedup':           'Finns redan - behöll den befintliga kopian',
    'documents.upload_dedup_enriched':  'Finns redan - slog samman dina nya detaljer med den befintliga kopian',
    'documents.drop_extras_one':        'Laddar upp "{name}" - en extra fil ignorerades',
    'documents.drop_extras_other':      'Laddar upp "{name}" - {count} extra filer ignorerades',
    'documents.entity_company':         'Företag: {name}',
    'documents.entity_person':          'Person: {name}',
    'documents.date_just_now':          'just nu',
    'documents.ago_min':                'för {count} min sedan',
    'documents.ago_h':                  'för {count} tim sedan',
    'documents.ago_d':                  'för {count} d sedan',
    'documents.drop_title':             'Släpp för att ladda upp',
    'documents.drop_hint':              'Vi tar den första filen',
    'documents.err_load':               'Det gick inte att läsa in dokument',
    'documents.err_load_generated':     'Det gick inte att läsa in genererade filer',
    'documents.err_delete':             'Det gick inte att ta bort dokumentet',
    'documents.err_visibility':         'Det gick inte att ändra synligheten',
    'documents.err_open':               'Det gick inte att öppna dokumentet',
    'documents.err_open_no_agent':      'Ingen agent är tillgänglig för att öppna dokumentet',

    // ── Mappar + Papperskorg (DocumentsView.vue) ──
    'documents.tab_trash':              'Papperskorg',
    'documents.folders_aria':           'Mappar',
    'documents.root_private':           'Mina dokument',
    'documents.root_team':              'Teamdokument',
    'documents.root_channels':          'Kanaler',
    'documents.folder_all':             'Alla',
    'documents.new_folder_tip':         'Ny mapp',
    'documents.new_folder_title':       'Ny mapp',
    'documents.new_folder_label':       'Mappnamn',
    'documents.new_folder_ph':          't.ex. Fakturor',
    'documents.folder_name_empty':      'Ange ett namn.',
    'documents.folder_created_toast':   'Mappen skapades',
    'documents.err_folder_create':      'Det gick inte att skapa mappen',
    'documents.rename_folder':          'Byt namn',
    'documents.rename_folder_title':    'Byt namn på mapp',
    'documents.rename_folder_label':    'Mappnamn',
    'documents.folder_renamed_toast':   'Mappen bytte namn',
    'documents.err_folder_rename':      'Det gick inte att byta namn på mappen',
    'documents.delete_folder':          'Ta bort',
    'documents.delete_folder_title':    'Ta bort "{name}"?',
    'documents.delete_folder_body':     'Detta flyttar mappen, dess undermappar och allt innehåll till papperskorgen.',
    'documents.delete_folder_confirm':  'Flytta till papperskorgen',
    'documents.folder_trashed_toast':   'Mappen flyttades till papperskorgen',
    'documents.err_folder_delete':      'Det gick inte att ta bort mappen',
    'documents.err_load_folders':       'Det gick inte att läsa in mappar',
    'documents.folder_doc_count':       '{count} filer i den här mappen',
    'documents.expand_folder':          'Expandera',
    'documents.collapse_folder':        'Fäll ihop',
    'documents.folder_actions_aria':    'Mappåtgärder',
    'documents.move_to_folder':         'Flytta till mapp…',
    'documents.move_picker_title':      'Flytta till mapp',
    'documents.move_root':              'Rot',
    'documents.moved_toast':            'Flyttad',
    'documents.err_move':               'Det gick inte att flytta filen',
    'documents.share_team_title':       'Dela med ditt team?',
    'documents.share_team_body':        'Att lägga den i en teammapp delar den med hela ditt team. Fortsätta?',
    'documents.share_channel_title':    'Dela med {channel}?',
    'documents.share_channel_body':     'Att lägga den i en kanalmapp delar den i {channel}. Fortsätta?',
    'documents.share_confirm':          'Dela',
    'documents.shared_toast':           'Filen delades',
    'documents.crumb_root':             'Alla',
    'documents.empty_folder_title':     'Den här mappen är tom',
    'documents.empty_folder_body':      'Flytta filer hit, eller ladda upp i den här mappen.',
    'documents.trash_empty_title':      'Papperskorgen är tom',
    'documents.trash_empty_body':       'Borttagna filer hamnar här. De tas bort permanent när du raderar dem för gott.',
    'documents.restore':                'Återställ',
    'documents.restored_toast':         'Återställd',
    'documents.err_restore':            'Det gick inte att återställa filen',
    'documents.delete_forever':         'Ta bort för gott',
    'documents.purge_title':            'Ta bort "{name}" för gott?',
    'documents.purge_body':             'Detta raderar filen permanent. Detta kan inte ångras.',
    'documents.purge_confirm':          'Ta bort för gott',
    'documents.purge_folder_title':     'Ta bort mappen "{name}" för gott?',
    'documents.purge_folder_body':      'Detta raderar mappen permanent. Detta kan inte ångras.',
    'documents.purge_folder_body_files': 'Detta raderar mappen och dess {count} fil(er) permanent. Detta kan inte ångras.',
    'documents.purged_toast':           'Borttagen för gott',
    'documents.err_purge':              'Det gick inte att ta bort filen',
    'documents.search_trash_ph':        'Sök i papperskorgen…',
    'documents.preview_empty_trash':    'Välj en borttagen fil för att förhandsgranska den.',

    // ── web-ui ConnectView.vue ──
    'connect.title':                  'Anslut',
    'connect.subtitle':               'Lägg till HQ i dina verktyg och agera på det vi hittat för din arbetsyta.',
    'connect.channels_title':         'Kanaler',
    'connect.channels_lede':          'Lägg till HQ där ditt team redan arbetar.',
    'connect.connected':              'Ansluten',
    'connect.slack_connected_toast':  'Slack anslutet till den här arbetsytan.',
    'connect.slack_taken_toast':      'Den Slack-arbetsytan är redan ansluten till en annan HQ-arbetsyta.',
    'connect.slack_already_toast':    'Den här arbetsytan har redan Slack anslutet. Koppla från det först för att ansluta ett annat.',
    'connect.teams_claimed_toast':    'Microsoft verifierat. Lägg nu till HQ-appen i Teams.',
    'connect.teams_connected_toast':  'Teams är redan anslutet till den här arbetsytan.',
    'connect.teams_taken_toast':      'Den Microsoft-organisationen är redan ansluten till en annan HQ-arbetsyta.',
    'connect.teams_error_toast':      'Microsoft-inloggningen slutfördes inte. Försök igen.',
    'connect.teams_verified_lead':    'Verifierat. Lägg till HQ i Teams för att slutföra:',
    'connect.teams_step_download':    'Ladda ner apppaketet HQ för Teams.',
    'connect.teams_step_upload':      'I Teams administrationscenter -> Hantera appar (eller Appar -> Hantera dina appar -> Ladda upp en anpassad app), ladda upp paketet.',
    'connect.teams_step_install':     'Öppna HQ i Teams och installera det - det ansluter till den här arbetsytan automatiskt.',
    'connect.teams_download_pkg':     'Ladda ner HQ för Teams',
    'connect.teams_pkg_unavailable':  'Din HQ-kontakt delar apppaketet att ladda upp.',
    'connect.coming_soon':            'Kommer snart',
    'connect.slack_sub':              'Ta in dina agenter i kanalerna ditt team redan arbetar i.',
    'connect.slack_reconnect':        'Anslut igen',
    'connect.slack_reconnect_hint':   'Auktorisera Slack igen för att ge HQ nya behörigheter.',
    'connect.teams_sub':              'Ta in dina agenter i Microsoft Teams.',
    'connect.teams_add':              'Lägg till i Teams',
    'connect.ext_title':              'Webbläsartillägg',
    'connect.ext_sub':                'Låt agenter läsa och agera på sidorna du tittar på.',
    'connect.ext_download':           'Ladda ner tillägget',
    'connect.ext_web_store':          'Hämta det från butiken',
    'connect.recommended_title':      'Rekommenderat för dig',
    'connect.recommended_lede':       'Färdigheter och integrationer valda utifrån vad vi hittat om din arbetsyta.',
    'connect.recommended_empty_title':'Inget att rekommendera just nu',
    'connect.recommended_empty_body': 'Allteftersom vi lär oss mer om din arbetsyta visas förslag här.',
    'connect.detected_title':         'Upptäckt på din webbplats',
    'connect.detected_lede':          'Signaler vi fångade upp när vi läste din publika webbplats och DNS.',
    'connect.detected_email_dns':     'E-post och DNS',
    'connect.detected_web_tech':      'Webbteknik',
    'connect.kind_skill':             'Färdighet',
    'connect.kind_integration':       'Integration',
    'connect.action_enable':          'Aktivera',
    'connect.action_setup':           'Konfigurera',
    'connect.priority_high':          'Hög',
    'connect.priority_medium':        'Medel',
    'connect.priority_low':           'Låg',
    'connect.dismiss':                'Avfärda',
    'connect.enabled_toast':          'Aktiverade {name}',
    'connect.err_enable':             'Det gick inte att aktivera det',
    'connect.load_error_title':       'Det gick inte att läsa in Anslut',
    'connect.load_error_body':        'Något gick fel när din arbetsytas status hämtades. Försök igen om en stund.',

    // ── web-ui DeveloperAppsView.vue ──
    'devApps.title':                  'Utvecklarappar',
    'devApps.subtitle':               'OAuth 2.1-klienter du har registrerat för att integrera med HQ.',
    'devApps.new_app':                'Ny app',
    'devApps.hint_base_before':       'API-basen är',
    'devApps.hint_minted_before':     '. Token utfärdas vid',
    'devApps.hint_api_before':        '. Den fullständiga referensen finns i',
    'devApps.hint_openapi_link':      'OpenAPI-specen',
    'devApps.hint_pat_before':        'För en personlig token i stället för en full app, använd',
    'devApps.hint_account_link':      'ditt konto',
    'devApps.reveal_head':            'Spara dessa nu - hemligheten visas bara en gång.',
    'devApps.client_id':              'Klient-ID',
    'devApps.client_secret':          'Klienthemlighet',
    'devApps.reg_token':              'Registreringstoken',
    'devApps.copy':                   'Kopiera',
    'devApps.copied':                 'Kopierat',
    'devApps.saved_them':             'Jag har sparat dem',
    'devApps.form_title_register':    'Registrera en app',
    'devApps.form_title_edit':        'Redigera app',
    'devApps.field_name_label':       'Namn',
    'devApps.field_name_hint':        'Visas för användare på samtyckesskärmen.',
    'devApps.field_name_ph':          'Min integration',
    'devApps.field_redirects_label':  'Omdirigerings-URI:er',
    'devApps.field_redirects_hint':   'En per rad. Hit skickar vi tillbaka användare efter att de godkänt.',
    'devApps.client_type_label':      'Klienttyp',
    'devApps.client_public':          'Offentlig',
    'devApps.client_confidential':    'Konfidentiell',
    'devApps.client_hint_fixed':      'Klienttypen är fastställd vid registrering och kan inte ändras.',
    'devApps.client_hint_public':     'För appar som inte kan hålla en hemlighet (ensidesappar, inbyggda klienter). Använder PKCE, ingen hemlighet.',
    'devApps.client_hint_confidential':'För serverappar som kan lagra en hemlighet säkert. En klienthemlighet utfärdas.',
    'devApps.scopes_label':           'Behörigheter',
    'devApps.scopes_hint':            'Behörigheterna den här appen kan begära från användare.',
    'devApps.cancel':                 'Avbryt',
    'devApps.saving':                 'Sparar…',
    'devApps.registering':            'Registrerar…',
    'devApps.save_changes':           'Spara ändringar',
    'devApps.register_app':           'Registrera app',
    'devApps.empty_title':            'Inga utvecklarappar än',
    'devApps.empty_body':             'Registrera en OAuth-klient för att integrera din egen app med HQ.',
    'devApps.redirect_uris_one':      '{count} omdirigerings-URI',
    'devApps.redirect_uris_other':    '{count} omdirigerings-URI:er',
    'devApps.registered':             'Registrerad {date}',
    'devApps.edit':                   'Redigera',
    'devApps.edit_app_tip':           'Redigera app',
    'devApps.delete':                 'Ta bort',
    'devApps.delete_app_tip':         'Ta bort app',
    'devApps.delete_title':           'Ta bort app?',
    'devApps.delete_body':            'Detta tar permanent bort "{name}" och återkallar dess uppgifter. Detta kan inte ångras.',
    'devApps.delete_confirm':         'Ta bort app',
    'devApps.deleted_toast':          'Appen togs bort',
    'devApps.updated_toast':          'Appen uppdaterades',
    'devApps.registered_toast':       'Appen registrerades',
    'devApps.err_register':           'Det gick inte att registrera appen',
    'devApps.err_update':             'Det gick inte att uppdatera appen',
    'devApps.err_delete':             'Det gick inte att ta bort appen',
    'devApps.err_copy':               'Det gick inte att kopiera till urklipp',

    // ── web-ui DevToolsView.vue ──
    'devtools.title':                 'Utvecklarverktyg',
    'devtools.subtitle':              'Starta engångstenanter, gå igenom onboarding-ytorna och riv ner testdata.',
    'devtools.disabled_before':       'Inaktiverat. Sätt',
    'devtools.disabled_after':        'på controlplane för att aktivera dessa verktyg.',
    'devtools.disabled_toast':        'Utvecklarverktyg är inaktiverade på den här installationen.',
    'devtools.create_demo':           'Skapa demotenant',
    'devtools.send_notification':     'Skicka testnotis',
    'devtools.refresh':               'Uppdatera',
    'devtools.purge':                 'Rensa demotenanter',
    'devtools.col_slug':              'Slug',
    'devtools.col_name':              'Namn',
    'devtools.col_source':            'Källa',
    'devtools.col_onboarded':         'Onboardad',
    'devtools.col_demo':              'Demo',
    'devtools.yes':                   'Ja',
    'devtools.no':                    'Nej',
    'devtools.inspect_tip':           'Inspektera profilering',
    'devtools.delete_tip':            'Ta bort tenant',
    'devtools.protected':             'Skyddad',
    'devtools.inspector_title':       'Profileringsinspektör',
    'devtools.refetch_tip':           'Hämta profil igen',
    'devtools.loading_profile':       'Läser in profil…',
    'devtools.sec_domain':            'Domän',
    'devtools.domain_confirmed':      'Bekräftad',
    'devtools.domain_unconfirmed':    'Obekräftad',
    'devtools.onboarding_done':       'Onboardad',
    'devtools.onboarding_open':       'Onboarding pågår',
    'devtools.sec_enrichment':        'Företagsberikning',
    'devtools.enrichment_industry':   'Bransch:',
    'devtools.enrichment_segment':    'Segment:',
    'devtools.enrichment_size':       'Storlek:',
    'devtools.sec_brand':             'Varumärkescrawl',
    'devtools.pages_crawled':         '{count} sidor crawlade',
    'devtools.no_crawl':              'Ingen crawl än.',
    'devtools.sec_signals':           'Upptäckta signaler',
    'devtools.no_signals':            'Inga signaler än.',
    'devtools.bucket_email_dns':      'E-post och DNS',
    'devtools.bucket_web_tech':       'Webbteknik',
    'devtools.sec_recommendations':   'Rekommendationer',
    'devtools.no_recommendations':    'Inga rekommendationer än.',
    'devtools.sec_gaps':              'Otillgodosedda behov',
    'devtools.no_gaps':               'Inga luckor upptäckta.',
    'devtools.no_tenants':            'Inga tenanter.',
    'devtools.demand_toggle':         'Vad kunderna behöver härnäst',
    'devtools.demand_sub':            'För varje verktyg kunderna använder som HQ saknar koppling till, hur många distinkta tenanter vill ha det.',
    'devtools.refresh_demand_tip':    'Uppdatera efterfrågan',
    'devtools.loading_demand':        'Läser in efterfrågan…',
    'devtools.col_category':          'Kategori',
    'devtools.col_tool':              'Verktyg',
    'devtools.col_tenants':           'Tenanter',
    'devtools.no_demand':             'Inga efterfrågesignaler än.',
    'devtools.demo_created_toast':    'Demotenanten "{slug}" skapades',
    'devtools.err_create_demo':       'Det gick inte att skapa en demotenant',
    'devtools.notify_sent_toast':     'Testnotis skickad',
    'devtools.notify_signin':         'Logga in för att skicka en testnotis.',
    'devtools.err_notify':            'Det gick inte att skicka testnotisen',
    'devtools.purge_title':           'Rensa demotenanter?',
    'devtools.purge_body':            'Detta tar bort varje demotenant och dess data. Detta kan inte ångras.',
    'devtools.purge_confirm':         'Rensa',
    'devtools.purged_toast':          'Rensade {count} demotenanter',
    'devtools.err_purge':             'Det gick inte att rensa demotenanter',
    'devtools.delete_title':          'Ta bort tenanten "{slug}"?',
    'devtools.delete_body':           'Detta tar bort tenanten och all dess data. Detta kan inte ångras.',
    'devtools.delete_confirm':        'Ta bort tenant',
    'devtools.deleted_toast':         'Tenanten "{slug}" togs bort',
    'devtools.err_delete':            'Det gick inte att ta bort tenanten',
    'devtools.err_load_tenants':      'Det gick inte att läsa in tenanter',
    'devtools.err_load_profile':      'Det gick inte att läsa in profilen',
    'devtools.err_load_demand':       'Det gick inte att läsa in efterfrågan',
  },
};
